import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError, API_BASE } from "../api/client";
import { useI18n, formatDateTime, formatMoney } from "../i18n";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader, Card, Badge, EmptyState } from "../components/shared";
import {
  Stethoscope, Plus, X, Trash2, Eye, FileText, Download, Edit3,
  Activity, Pill, ClipboardList, Search, Filter, Archive,
} from "lucide-react";
import { toast } from "sonner";
import BulkArchiveDialog, { ArchiveRowButton, ArchiveTabs } from "../components/Archive";

export default function Consultations() {
  const { t, lang } = useI18n();
  const { user, tenant } = useAuth();
  const nav = useNavigate();
  const cur = tenant?.currency || "XOF";

  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [products, setProducts] = useState([]);
  const [doctors, setDoctors] = useState([]);

  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [detail, setDetail] = useState(null); // enriched consultation

  // Filters
  const [filterPatient, setFilterPatient] = useState("");
  const [filterDoctor, setFilterDoctor] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  const [archiveView, setArchiveView] = useState("active");
  const [archivedCount, setArchivedCount] = useState(0);
  const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false);

  const canWrite = ["owner", "doctor", "nurse"].includes(user?.role);
  const canInvoice = ["owner", "secretary", "cashier", "doctor"].includes(user?.role);

  function emptyForm() {
    return {
      patient_id: "", appointment_id: "", reason: "",
      vitals: { weight: "", temperature: "", blood_pressure_sys: "", blood_pressure_dia: "", heart_rate: "" },
      notes: "", diagnosis: "", treatment: "",
      prescription: [],
    };
  }

  const load = async () => {
    const params = {};
    if (filterPatient) params.patient_id = filterPatient;
    if (filterDoctor) params.doctor_id = filterDoctor;
    if (filterFrom) params.from = filterFrom;
    if (filterTo) params.to = filterTo + "T23:59:59";
    if (filterStatus !== "all") params.status = filterStatus;
    if (archiveView === "archived") params.archived = "true";
    const [list, arch] = await Promise.all([
      api.get("/consultations", { params }),
      api.get("/consultations", { params: { archived: "true" } }),
    ]);
    setItems(list.data);
    setArchivedCount(arch.data.length);
  };

  useEffect(() => {
    (async () => {
      const [p, pr, d] = await Promise.all([
        api.get("/patients", { params: { limit: 500 } }),
        api.get("/stock/products"),
        api.get("/users", { params: { role: "doctor" } }),
      ]);
      setPatients(p.data);
      setProducts(pr.data);
      setDoctors(d.data);
      load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const h = setTimeout(() => load(), 200);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPatient, filterDoctor, filterFrom, filterTo, filterStatus, archiveView]);

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const pid = searchParams.get("patient_id");
    if (pid) {
      setForm((f) => ({ ...f, patient_id: pid }));
      setOpenNew(true);
      const next = new URLSearchParams(searchParams);
      next.delete("patient_id");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addMed = () => setForm({
    ...form,
    prescription: [...form.prescription, { name: "", dosage: "", frequency: "", duration: "", quantity: 0, product_id: "" }],
  });
  const updateMed = (i, patch) => {
    const next = [...form.prescription];
    next[i] = { ...next[i], ...patch };
    setForm({ ...form, prescription: next });
  };
  const removeMed = (i) => setForm({ ...form, prescription: form.prescription.filter((_, k) => k !== i) });

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        patient_id: form.patient_id,
        appointment_id: form.appointment_id || null,
        reason: form.reason,
        notes: form.notes,
        diagnosis: form.diagnosis,
        treatment: form.treatment,
        vitals: {
          weight: form.vitals.weight ? parseFloat(form.vitals.weight) : null,
          temperature: form.vitals.temperature ? parseFloat(form.vitals.temperature) : null,
          blood_pressure_sys: form.vitals.blood_pressure_sys ? parseInt(form.vitals.blood_pressure_sys) : null,
          blood_pressure_dia: form.vitals.blood_pressure_dia ? parseInt(form.vitals.blood_pressure_dia) : null,
          heart_rate: form.vitals.heart_rate ? parseInt(form.vitals.heart_rate) : null,
        },
        prescription: form.prescription.map((p) => ({
          name: p.name, dosage: p.dosage, frequency: p.frequency, duration: p.duration,
          quantity: p.quantity ? parseInt(p.quantity) : 0,
          product_id: p.product_id || null,
        })),
      };

      if (editingId) {
        await api.patch(`/consultations/${editingId}`, payload);
      } else {
        const { data: c } = await api.post("/consultations", payload);
        // Decrement stock for prescriptions linked to products
        for (const p of payload.prescription) {
          if (p.product_id && p.quantity > 0) {
            try {
              await api.post("/stock/movements", {
                product_id: p.product_id, type: "OUT", subtype: "consumption",
                quantity: p.quantity, reason: `Consultation ${c.id.slice(0, 8)}`,
                patient_id: form.patient_id, consultation_id: c.id,
              });
            } catch (e) { /* non-blocking */ }
          }
        }
      }
      toast.success(t("common.success"));
      setForm(emptyForm());
      setEditingId(null);
      setOpenNew(false);
      load();
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/consultations/${id}`);
      setDetail(data);
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    }
  };

  const startEdit = (c) => {
    setForm({
      patient_id: c.patient_id, appointment_id: c.appointment_id || "",
      reason: c.reason || "",
      vitals: {
        weight: c.vitals?.weight ?? "",
        temperature: c.vitals?.temperature ?? "",
        blood_pressure_sys: c.vitals?.blood_pressure_sys ?? "",
        blood_pressure_dia: c.vitals?.blood_pressure_dia ?? "",
        heart_rate: c.vitals?.heart_rate ?? "",
      },
      notes: c.notes || "", diagnosis: c.diagnosis || "", treatment: c.treatment || "",
      prescription: c.prescription || [],
    });
    setEditingId(c.id);
    setDetail(null);
    setOpenNew(true);
  };

  const downloadPdf = async (id) => {
    try {
      const token = localStorage.getItem("mediflow_token");
      const res = await fetch(`${API_BASE}/consultations/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `consultation-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(t("common.error"));
    }
  };

  const createInvoice = (c) => {
    // Pre-fill and navigate
    nav(`/app/invoices?patient_id=${c.patient_id}`);
  };

  const clearFilters = () => {
    setFilterPatient(""); setFilterDoctor(""); setFilterFrom(""); setFilterTo(""); setFilterStatus("all");
  };

  const input = "w-full rounded-xl border border-[var(--border-default)] bg-white px-4 h-12 text-base focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none";
  const inputSm = "w-full rounded-xl border border-[var(--border-default)] bg-white px-3 h-11 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none";

  const activeFiltersCount = [filterPatient, filterDoctor, filterFrom, filterTo, filterStatus !== "all" ? "1" : ""].filter(Boolean).length;

  return (
    <div>
      <PageHeader
        title={t("consultations.title")}
        subtitle={`${items.length} consultation(s)`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <ArchiveTabs value={archiveView} onChange={setArchiveView} activeCount={archiveView === "active" ? items.length : null} archivedCount={archivedCount} />
            {archiveView === "active" && (
              <>
                <button onClick={() => setBulkArchiveOpen(true)} data-testid="bulk-archive-btn"
                  className="h-11 px-4 rounded-xl border border-[var(--border-default)] bg-white hover:bg-subtle font-semibold text-sm flex items-center gap-2">
                  <Archive className="w-4 h-4" /> Archiver…
                </button>
                {canWrite && (
                  <button onClick={() => { setEditingId(null); setForm(emptyForm()); setOpenNew(true); }} data-testid="new-consult-btn"
                    className="h-12 px-5 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold flex items-center gap-2">
                    <Plus className="w-5 h-5" /> {t("consultations.new")}
                  </button>
                )}
              </>
            )}
          </div>
        }
      />

      {/* Filters */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setShowFilters(!showFilters)}
          data-testid="toggle-filters"
          className="h-11 px-4 rounded-xl bg-white border border-[var(--border-default)] font-semibold text-sm flex items-center gap-2 hover:bg-subtle"
        >
          <Filter className="w-4 h-4" />
          Filtres
          {activeFiltersCount > 0 && <Badge tone="brand">{activeFiltersCount}</Badge>}
        </button>
        {["all", "completed", "invoiced"].map((s) => (
          <button key={s} onClick={() => setFilterStatus(s)} data-testid={`filter-status-${s}`}
            className={`h-11 px-4 rounded-xl font-semibold text-sm border transition-colors
              ${filterStatus === s ? "bg-brand text-white border-brand" : "bg-white text-ink-muted border-[var(--border-default)] hover:bg-subtle"}`}>
            {t(`consultations.filter${s === "all" ? "All" : s === "completed" ? "Completed" : "Invoiced"}`)}
          </button>
        ))}
        {activeFiltersCount > 0 && (
          <button onClick={clearFilters} className="h-11 px-3 rounded-xl text-ink-muted text-sm hover:bg-subtle">
            {t("consultations.clearFilters")}
          </button>
        )}
      </div>

      {showFilters && (
        <Card className="mb-6 p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="block mb-1 text-xs font-semibold text-ink-muted uppercase">{t("consultations.filterPatient")}</label>
              <select className={inputSm} value={filterPatient} onChange={(e) => setFilterPatient(e.target.value)} data-testid="filter-patient">
                <option value="">{t("common.all")}</option>
                {patients.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-ink-muted uppercase">{t("consultations.filterDoctor")}</label>
              <select className={inputSm} value={filterDoctor} onChange={(e) => setFilterDoctor(e.target.value)} data-testid="filter-doctor">
                <option value="">{t("common.all")}</option>
                {doctors.map((d) => <option key={d.id} value={d.id}>Dr {d.first_name} {d.last_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-ink-muted uppercase">{t("consultations.filterFrom")}</label>
              <input type="date" className={inputSm} value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} data-testid="filter-from" />
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-ink-muted uppercase">{t("consultations.filterTo")}</label>
              <input type="date" className={inputSm} value={filterTo} onChange={(e) => setFilterTo(e.target.value)} data-testid="filter-to" />
            </div>
          </div>
        </Card>
      )}

      {/* List */}
      <Card className="overflow-hidden">
        {items.length === 0 ? (
          <EmptyState icon={Stethoscope} title={t("common.noData")} />
        ) : (
          <ul className="divide-y divide-[var(--border-default)]">
            {items.map((c) => {
              const statusTone = c.computed_status === "invoiced" ? "success" : "info";
              const statusLabel = c.computed_status === "invoiced" ? t("consultations.statusInvoiced") : t("consultations.statusCompleted");
              return (
                <li key={c.id} onClick={() => openDetail(c.id)} role="button" tabIndex={0}
                  data-testid={`cons-row-${c.id}`}
                  className="p-5 hover:bg-subtle/40 cursor-pointer transition-colors flex flex-col lg:flex-row lg:items-start gap-4">
                  <div className="w-11 h-11 rounded-full bg-brand/10 text-brand flex items-center justify-center shrink-0">
                    <Stethoscope className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-heading font-bold text-ink truncate">
                        {c.patient ? `${c.patient.first_name} ${c.patient.last_name}` : "—"}
                      </span>
                      <Badge tone={statusTone}>{statusLabel}</Badge>
                      {c.invoice && (
                        <Badge tone="neutral" className="font-mono">{c.invoice.invoice_number}</Badge>
                      )}
                    </div>
                    <div className="text-sm text-ink-muted flex items-center gap-3 flex-wrap mb-2">
                      <span>Dr {c.doctor?.first_name} {c.doctor?.last_name}</span>
                      {c.doctor?.specialty && <span>· {t(`specialties.${c.doctor.specialty}`)}</span>}
                      <span>· {formatDateTime(c.created_at, lang)}</span>
                    </div>
                    {c.reason && <div className="text-sm text-ink"><span className="text-ink-muted font-semibold mr-1">{t("consultations.reason")}:</span>{c.reason}</div>}
                    {c.diagnosis && <div className="text-sm text-ink mt-1"><span className="text-ink-muted font-semibold mr-1">{t("consultations.diagnosis")}:</span>{c.diagnosis}</div>}
                    {c.prescription?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {c.prescription.map((p, i) => (
                          <span key={i} className="px-2 py-0.5 rounded-md bg-brand/10 text-brand text-xs font-semibold">
                            <Pill className="w-3 h-3 inline mr-0.5" /> {p.name}{p.quantity ? ` × ${p.quantity}` : ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => openDetail(c.id)} data-testid={`cons-view-${c.id}`}
                      className="h-9 px-3 rounded-lg bg-subtle hover:bg-brand/10 hover:text-brand text-ink text-xs font-semibold flex items-center gap-1">
                      <Eye className="w-3.5 h-3.5" /> {t("consultations.actView")}
                    </button>
                    {canWrite && (
                      <button onClick={() => startEdit(c)} data-testid={`cons-edit-${c.id}`}
                        className="h-9 px-3 rounded-lg bg-[#2563eb]/10 text-[#1e40af] hover:bg-[#2563eb]/20 text-xs font-semibold flex items-center gap-1">
                        <Edit3 className="w-3.5 h-3.5" /> {t("consultations.actEdit")}
                      </button>
                    )}
                    {canInvoice && !c.invoice && (
                      <button onClick={() => createInvoice(c)} data-testid={`cons-inv-${c.id}`}
                        className="h-9 px-3 rounded-lg bg-terracotta/10 text-terracotta hover:bg-terracotta/20 text-xs font-semibold flex items-center gap-1">
                        <FileText className="w-3.5 h-3.5" /> {t("consultations.actInvoice")}
                      </button>
                    )}
                    <button onClick={() => downloadPdf(c.id)} data-testid={`cons-pdf-${c.id}`}
                      className="h-9 px-3 rounded-lg bg-brand text-white hover:bg-brand-hover text-xs font-semibold flex items-center gap-1">
                      <Download className="w-3.5 h-3.5" /> {t("consultations.actPdf")}
                    </button>
                    <ArchiveRowButton kind="consultations" itemId={c.id} archived={archiveView === "archived"} onDone={load} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* DETAIL MODAL */}
      {detail && (
        <DetailModal
          c={detail}
          onClose={() => setDetail(null)}
          onEdit={() => startEdit(detail)}
          onInvoice={() => createInvoice(detail)}
          onPdf={() => downloadPdf(detail.id)}
          canWrite={canWrite}
          canInvoice={canInvoice}
          cur={cur}
          t={t}
          lang={lang}
        />
      )}

      {/* NEW / EDIT MODAL */}
      {openNew && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[var(--border-default)] sticky top-0 bg-white z-10">
              <h3 className="font-heading font-bold text-xl">
                {editingId ? t("consultations.actEdit") : t("consultations.new")}
              </h3>
              <button onClick={() => { setOpenNew(false); setEditingId(null); }} className="p-2 rounded-lg hover:bg-subtle"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-5">
              <div>
                <label className="block mb-2 text-sm font-semibold">{t("appointments.patient")}</label>
                <select required className={input} value={form.patient_id} onChange={(e) => setForm({ ...form, patient_id: e.target.value })} data-testid="cs-patient" disabled={!!editingId}>
                  <option value="">—</option>
                  {patients.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                </select>
              </div>

              <div>
                <label className="block mb-2 text-sm font-semibold">{t("consultations.reason")}</label>
                <input className={input} placeholder="Ex: Mal de gorge depuis 3 jours" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="cs-reason" />
              </div>

              <div>
                <div className="font-heading font-bold text-ink mb-3">{t("consultations.vitals")}</div>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                  <FieldSmall label={t("consultations.weight")}><input className={input} value={form.vitals.weight} onChange={(e) => setForm({ ...form, vitals: { ...form.vitals, weight: e.target.value } })} /></FieldSmall>
                  <FieldSmall label={t("consultations.temperature")}><input className={input} value={form.vitals.temperature} onChange={(e) => setForm({ ...form, vitals: { ...form.vitals, temperature: e.target.value } })} /></FieldSmall>
                  <FieldSmall label={`${t("consultations.bp")} (sys)`}><input className={input} value={form.vitals.blood_pressure_sys} onChange={(e) => setForm({ ...form, vitals: { ...form.vitals, blood_pressure_sys: e.target.value } })} /></FieldSmall>
                  <FieldSmall label={`${t("consultations.bp")} (dia)`}><input className={input} value={form.vitals.blood_pressure_dia} onChange={(e) => setForm({ ...form, vitals: { ...form.vitals, blood_pressure_dia: e.target.value } })} /></FieldSmall>
                  <FieldSmall label={t("consultations.heartRate")}><input className={input} value={form.vitals.heart_rate} onChange={(e) => setForm({ ...form, vitals: { ...form.vitals, heart_rate: e.target.value } })} /></FieldSmall>
                </div>
              </div>

              <div>
                <label className="block mb-2 text-sm font-semibold">{t("consultations.observations")}</label>
                <textarea rows={3} className={`${input} h-auto py-3`} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cs-notes" placeholder="Examen clinique, symptômes observés…" />
              </div>

              <div>
                <label className="block mb-2 text-sm font-semibold">{t("consultations.diagnosis")}</label>
                <input className={input} value={form.diagnosis} onChange={(e) => setForm({ ...form, diagnosis: e.target.value })} data-testid="cs-diagnosis" placeholder="Ex: Angine bactérienne" />
              </div>

              <div>
                <label className="block mb-2 text-sm font-semibold">{t("consultations.treatment")}</label>
                <textarea rows={2} className={`${input} h-auto py-3`} value={form.treatment} onChange={(e) => setForm({ ...form, treatment: e.target.value })} data-testid="cs-treatment" placeholder="Recommandations, posologie, repos…" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-heading font-bold text-ink">{t("consultations.prescription")}</div>
                  <button type="button" onClick={addMed} data-testid="cs-add-med"
                    className="px-3 py-1.5 rounded-lg bg-brand/10 text-brand text-xs font-semibold hover:bg-brand/15">
                    + {t("consultations.addMed")}
                  </button>
                </div>
                <div className="space-y-3">
                  {form.prescription.map((p, i) => (
                    <div key={i} className="p-3 rounded-xl border border-[var(--border-default)] grid grid-cols-1 sm:grid-cols-6 gap-2">
                      <select className={`${input} h-11`} value={p.product_id || ""} onChange={(e) => {
                        const prod = products.find((x) => x.id === e.target.value);
                        updateMed(i, { product_id: e.target.value, name: prod?.name || p.name });
                      }}>
                        <option value="">— {t("consultations.medName")}</option>
                        {products.map((x) => <option key={x.id} value={x.id}>{x.name} (stock: {x.stock_quantity})</option>)}
                      </select>
                      <input className={`${input} h-11`} placeholder={t("consultations.medName")} value={p.name} onChange={(e) => updateMed(i, { name: e.target.value })} />
                      <input className={`${input} h-11`} placeholder={t("consultations.dosage")} value={p.dosage} onChange={(e) => updateMed(i, { dosage: e.target.value })} />
                      <input className={`${input} h-11`} placeholder={t("consultations.frequency")} value={p.frequency} onChange={(e) => updateMed(i, { frequency: e.target.value })} />
                      <input className={`${input} h-11`} placeholder={t("consultations.duration")} value={p.duration} onChange={(e) => updateMed(i, { duration: e.target.value })} />
                      <div className="flex gap-2">
                        <input type="number" min={0} className={`${input} h-11 w-20`} placeholder="Qté" value={p.quantity} onChange={(e) => updateMed(i, { quantity: e.target.value })} />
                        <button type="button" onClick={() => removeMed(i)} className="p-2 rounded-lg text-terracotta hover:bg-terracotta/10">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2 sticky bottom-0 bg-white pb-1">
                <button type="button" onClick={() => { setOpenNew(false); setEditingId(null); }} className="flex-1 h-12 rounded-xl border border-[var(--border-default)] font-semibold">{t("common.cancel")}</button>
                <button type="submit" data-testid="cs-save" className="flex-1 h-12 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold">{t("consultations.save")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {bulkArchiveOpen && (
        <BulkArchiveDialog kind="consultations" doctors={doctors}
          onClose={() => setBulkArchiveOpen(false)}
          onDone={() => { setBulkArchiveOpen(false); load(); }} />
      )}
    </div>
  );
}

function FieldSmall({ label, children }) {
  return (
    <div>
      <label className="block mb-1 text-xs font-semibold text-ink-muted uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

function DetailModal({ c, onClose, onEdit, onInvoice, onPdf, canWrite, canInvoice, cur, t, lang }) {
  const vitals = c.vitals || {};
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 bg-black/50">
      <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-[var(--border-default)] sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-heading font-bold text-xl text-ink">{t("consultations.detail")}</h3>
            <div className="text-sm text-ink-muted">
              {c.patient?.first_name} {c.patient?.last_name} · Dr {c.doctor?.first_name} {c.doctor?.last_name} · {formatDateTime(c.created_at, lang)}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-subtle"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5">
          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            {canWrite && (
              <button onClick={onEdit} data-testid="detail-edit"
                className="h-10 px-4 rounded-xl bg-[#2563eb]/10 text-[#1e40af] hover:bg-[#2563eb]/20 text-sm font-bold flex items-center gap-1.5">
                <Edit3 className="w-4 h-4" /> {t("consultations.actEdit")}
              </button>
            )}
            {canInvoice && !c.invoice && (
              <button onClick={onInvoice} data-testid="detail-inv"
                className="h-10 px-4 rounded-xl bg-terracotta text-white hover:bg-terracotta-hover text-sm font-bold flex items-center gap-1.5">
                <FileText className="w-4 h-4" /> {t("consultations.actInvoice")}
              </button>
            )}
            <button onClick={onPdf} data-testid="detail-pdf"
              className="h-10 px-4 rounded-xl bg-brand text-white hover:bg-brand-hover text-sm font-bold flex items-center gap-1.5">
              <Download className="w-4 h-4" /> {t("consultations.actPdf")}
            </button>
            {c.invoice && (
              <Badge tone="success" className="!py-2 !px-3 !text-sm">
                {t("consultations.hasInvoice")}: {c.invoice.invoice_number} — {formatMoney(c.invoice.total, cur)}
              </Badge>
            )}
          </div>

          <Section icon={ClipboardList} title={t("consultations.reason")}>{c.reason || t("consultations.noReason")}</Section>

          <Section icon={Activity} title={t("consultations.vitals")}>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <Vital label="kg" val={vitals.weight} />
              <Vital label="°C" val={vitals.temperature} />
              <Vital label="BP sys" val={vitals.blood_pressure_sys} />
              <Vital label="BP dia" val={vitals.blood_pressure_dia} />
              <Vital label="HR" val={vitals.heart_rate} />
            </div>
          </Section>

          <Section icon={ClipboardList} title={t("consultations.observations")}>{c.notes || "—"}</Section>
          <Section icon={ClipboardList} title={t("consultations.diagnosis")}>{c.diagnosis || "—"}</Section>
          <Section icon={ClipboardList} title={t("consultations.treatment")}>{c.treatment || "—"}</Section>

          {c.prescription?.length > 0 && (
            <Section icon={Pill} title={t("consultations.prescription")}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase text-ink-muted">
                      <th className="text-left py-2 font-semibold">{t("consultations.medName")}</th>
                      <th className="text-left py-2 font-semibold">{t("consultations.dosage")}</th>
                      <th className="text-left py-2 font-semibold">{t("consultations.frequency")}</th>
                      <th className="text-left py-2 font-semibold">{t("consultations.duration")}</th>
                      <th className="text-right py-2 font-semibold">{t("consultations.quantity")}</th>
                      <th className="text-left py-2 font-semibold">Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {c.prescription.map((p, i) => {
                      const linkedMove = c.stock_movements?.find((m) => m.product_id === p.product_id);
                      return (
                        <tr key={i} className="border-t border-[var(--border-default)]">
                          <td className="py-2 font-semibold text-ink">{p.name}</td>
                          <td className="py-2 text-ink-muted">{p.dosage || "—"}</td>
                          <td className="py-2 text-ink-muted">{p.frequency || "—"}</td>
                          <td className="py-2 text-ink-muted">{p.duration || "—"}</td>
                          <td className="py-2 text-right stat-number font-bold">{p.quantity || 0}</td>
                          <td className="py-2">
                            {linkedMove ? (
                              <Badge tone="success" className="!text-[10px]">
                                ✓ {t("consultations.stockLinked")} (-{linkedMove.quantity})
                              </Badge>
                            ) : (
                              <span className="text-xs text-ink-faint">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-brand" />
        <h4 className="font-heading font-bold text-sm text-ink uppercase tracking-wider">{title}</h4>
      </div>
      <div className="pl-6 text-sm text-ink whitespace-pre-wrap">{children}</div>
    </div>
  );
}

function Vital({ label, val }) {
  return (
    <div className="p-3 rounded-xl bg-subtle text-center">
      <div className="stat-number font-bold text-ink text-lg">{val ?? "—"}</div>
      <div className="text-[10px] text-ink-muted uppercase tracking-wider">{label}</div>
    </div>
  );
}
